import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf, Trees, HandHeart, ShieldCheck, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import {
  HERO_AMAZON,
  RIVER_SUNSET,
  COPAIBA_OIL,
  WOOD_FURNITURE,
  INDIGENOUS_CRAFTS,
  ECO_LOJA_LOGO,
  PHOTO_INVENTORY,
  PHOTO_MOVELARIA,
  PHOTO_BASE_KM117,
  priceLabel,
  formatDate,
} from "../lib/constants";

export default function Home() {
  const [news, setNews] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/news?limit=3").then((r) => setNews(r.data)).catch(() => {});
    api.get("/products?limit=3").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <img
          src={HERO_AMAZON}
          alt="Floresta amazônica vista de cima"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-900 via-forest-900/60 to-forest-900/20" />
        <div className="absolute inset-0 noise-overlay pointer-events-none" />

        <img
          src="https://customer-assets-lqy194kg.emergentagent.net/job_cooperative-web-1/artifacts/c2g4qm8d_SOMOS%20COOP.PNG"
          alt="Personagens SOMOS COOP da COOMFLONA segurando placa"
          data-testid="hero-somoscoop-badge"
          className="hidden md:block absolute right-4 lg:right-8 bottom-24 lg:bottom-28 z-10 w-[40%] lg:w-[36%] xl:w-[32%] max-w-[620px] object-contain pointer-events-none animate-fade-up drop-shadow-[0_20px_40px_rgba(0,0,0,0.45)]"
          style={{ background: "transparent" }}
        />

        <div className="container-page relative z-10 pb-20 md:pb-32 text-cream-100">
          <div className="max-w-3xl md:max-w-[56%] animate-fade-up">
            <div className="inline-flex items-center gap-2 eyebrow !text-copaiba-300 mb-6">
              <Leaf className="w-3.5 h-3.5" /> Bioeconomia da floresta
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              A floresta em pé,<br />
              <span className="italic text-copaiba-300">viva</span> em cada produto.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-cream-100/85 max-w-2xl leading-relaxed">
              Somos a <strong>COOMFLONA</strong> — Cooperativa Mista da Flona do Tapajós. Há <strong>21 anos</strong>, unimos
              <strong> 305 cooperados</strong> e beneficiamos mais de <strong>1.000 famílias</strong> da região da Floresta
              Nacional do Tapajós, em Belterra (PA), promovendo o manejo florestal 100% comunitário.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/eco-loja" data-testid="hero-shop-cta" className="btn-primary bg-copaiba-500 text-forest-900 hover:bg-copaiba-300">
                Conhecer a Eco Loja
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/sobre" data-testid="hero-about-cta" className="btn-outline border-cream-100 text-cream-100 hover:bg-cream-100 hover:text-forest-900">
                Nossa história
              </Link>
            </div>
          </div>
        </div>

        {/* stats strip */}
        <div className="absolute left-0 right-0 bottom-0 z-10 border-t border-cream-100/10 backdrop-blur-xl bg-forest-900/40">
          <div className="container-page grid grid-cols-2 md:grid-cols-4 divide-x divide-cream-100/10">
            {[
              { k: "21", v: "anos de cooperativa" },
              { k: "305", v: "cooperados ativos" },
              { k: "1.000+", v: "famílias beneficiadas" },
              { k: "100%", v: "manejo comunitário" },
            ].map((s, i) => (
              <div key={i} className="py-5 px-4 text-cream-100">
                <div className="font-display text-3xl md:text-4xl text-copaiba-300">{s.k}</div>
                <div className="text-[11px] md:text-xs uppercase tracking-[0.22em] text-cream-100/70 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-24 md:py-32">
        <div className="container-page grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-5">Quem somos</div>
            <h2 className="font-display text-4xl md:text-5xl text-forest-900 leading-[1.05]">
              Nascemos das águas do Tapajós e dos caminhos da floresta.
            </h2>
            <div className="section-divider my-7" />
            <p className="text-base md:text-lg text-forest-900/80 leading-relaxed">
              Fundada em <strong>2005</strong>, a COOMFLONA nasceu da união de lideranças da Flona do Tapajós para promover
              o manejo sustentável da floresta, com apoio técnico do <strong>Projeto Promanejo</strong>. Hoje, somos
              <strong> 305 cooperados</strong> que beneficiam mais de <strong>1.000 famílias</strong> no município de Belterra
              (PA), fortalecendo a gestão comunitária dos recursos naturais e o desenvolvimento econômico e social da região.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-5">
              {[
                { i: Trees, t: "Manejo certificado" },
                { i: HandHeart, t: "Comércio justo" },
                { i: ShieldCheck, t: "Rastreabilidade" },
                { i: Sparkles, t: "Saber ancestral" },
              ].map((x, idx) => (
                <div key={idx} className="flex items-center gap-3 text-sm text-forest-900/85">
                  <x.i className="w-5 h-5 text-copaiba-700" />
                  {x.t}
                </div>
              ))}
            </div>
            <Link to="/sobre" data-testid="home-about-link" className="btn-outline mt-10">Saiba mais sobre nós</Link>
          </div>

          <div className="lg:col-span-7 grid grid-cols-6 grid-rows-6 gap-3 min-h-[520px]">
            <img src={PHOTO_BASE_KM117} alt="Base KM 117 da COOMFLONA" className="col-span-4 row-span-4 w-full h-full object-cover" />
            <img src={PHOTO_MOVELARIA} alt="Movelaria Anambé" className="col-span-2 row-span-3 w-full h-full object-cover" />
            <div className="col-span-2 row-span-3 bg-forest-700 text-cream-100 p-6 flex flex-col justify-between">
              <div className="eyebrow !text-copaiba-300">Belterra — Pará</div>
              <p className="font-display text-2xl leading-tight">Floresta Nacional do Tapajós — gestão 100% comunitária.</p>
            </div>
            <img src={PHOTO_INVENTORY} alt="Equipe em inventário florestal" className="col-span-6 row-span-2 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* ECO LOJA TEASER */}
      <section className="bg-cream-200 py-24 md:py-32 relative overflow-hidden">
        <div className="container-page relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div className="max-w-2xl">
              <div className="flex items-center gap-4 mb-4">
                <img src={ECO_LOJA_LOGO} alt="Eco Loja Tapajós" className="h-16 w-auto" />
                <div className="eyebrow">Eco Loja Tapajós</div>
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-forest-900 leading-[1.05]">
                Produtos que carregam a floresta em si.
              </h2>
              <p className="mt-5 text-base md:text-lg text-forest-900/80 leading-relaxed">
                Óleos vegetais amazônicos (copaíba, andiroba e outros), mel de abelhas da floresta, artesanato e o
                mobiliário autoral da <strong>Movelaria Anambé</strong> — todos produzidos pelos cooperados da COOMFLONA,
                com comércio justo e rastreabilidade total.
              </p>
            </div>
            <Link to="/eco-loja" data-testid="home-shop-all-link" className="btn-primary self-start md:self-end">
              Ver todos os produtos <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(products.length ? products : [
              { id: "a", slug: "oleo-de-copaiba", name: "Óleo de Copaíba", price: 0, image_url: COPAIBA_OIL, category: "oleos" },
              { id: "b", slug: "movelaria-anambe", name: "Móveis Anambé", price: 0, image_url: WOOD_FURNITURE, category: "movelaria" },
              { id: "c", slug: "artesanato", name: "Artesanato Amazônico", price: 0, image_url: INDIGENOUS_CRAFTS, category: "artesanato" },
            ]).slice(0, 3).map((p) => (
              <Link
                to={`/eco-loja/${p.slug}`}
                key={p.id}
                data-testid={`home-product-${p.slug}`}
                className="card-earthy group block"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={p.image_url}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 flex items-start justify-between gap-3">
                  <div>
                    <div className="eyebrow mb-1">{p.category}</div>
                    <div className="font-display text-2xl text-forest-900 leading-tight">{p.name}</div>
                  </div>
                  <div className="text-copaiba-700 font-medium">{priceLabel(p.price)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* NEWS TEASER */}
      <section className="py-24 md:py-32">
        <div className="container-page">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div className="max-w-2xl">
              <div className="eyebrow mb-4">Notícias da cooperativa</div>
              <h2 className="font-display text-4xl md:text-5xl text-forest-900 leading-[1.05]">
                O que acontece na floresta.
              </h2>
            </div>
            <Link to="/noticias" data-testid="home-news-all-link" className="btn-outline self-start md:self-end">
              Todas as notícias <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {news.map((n) => (
              <Link
                to={`/noticias/${n.slug}`}
                key={n.id}
                data-testid={`home-news-${n.slug}`}
                className="group block"
              >
                <div className="aspect-[4/3] overflow-hidden mb-5">
                  <img
                    src={n.image_url}
                    alt={n.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="eyebrow mb-2">{formatDate(n.created_at)} &middot; {n.category}</div>
                <h3 className="font-display text-2xl text-forest-900 leading-tight group-hover:text-copaiba-700 transition-colors">
                  {n.title}
                </h3>
                <p className="mt-3 text-forest-900/75 line-clamp-3">{n.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-forest-900 text-cream-100 overflow-hidden">
        <img src={RIVER_SUNSET} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-forest-900/70" />
        <div className="container-page relative py-24 md:py-32 text-center">
          <div className="eyebrow !text-copaiba-300 mb-4">Junte-se a nós</div>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-3xl mx-auto">
            Quando você consome da floresta, a floresta permanece viva.
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/eco-loja" data-testid="cta-visit-shop" className="btn-primary bg-copaiba-500 text-forest-900 hover:bg-copaiba-300">
              Comprar na Eco Loja
            </Link>
            <Link to="/contato" data-testid="cta-contact" className="btn-outline border-cream-100 text-cream-100 hover:bg-cream-100 hover:text-forest-900">
              Falar conosco
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
